import { Body } from "@hypercard/web-components";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateStipendStep } from "./types";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { StipendRuleFrequency } from "@common/utils/graphql";
import { Card } from "@/components/ui/card";
import { useRecoilState } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";
import { CurrencyInput } from "@common/components/CurrencyInput";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@common/components/DatePicker/DatePicker";
import { AnimatedProps, animated } from "@react-spring/web";
import { CSSProperties } from "react";
import { cn } from "@/utils/utils";
import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from "date-fns";

const isTodayOrAfter = (date: Date) => {
  const now = new Date();
  return isSameDay(date, now) || isAfter(date, now);
};
const getFormSchema = (
  isEditing: boolean,
  startsAtDefaultValue: Date = new Date(),
  expiresAtDefaultValue?: Date,
) =>
  z.object({
    frequency: z.enum(
      [
        StipendRuleFrequency.Daily,
        StipendRuleFrequency.OneTime,
        StipendRuleFrequency.Monthly,
      ],
      { required_error: "Select a frequency" },
    ),
    isBalanceForwardable: z.boolean(),
    amount: z.coerce
      .number({ message: "Enter a valid amount" })
      .positive("Amount must be greater than $0.00"),
    startsAt: z
      .date({
        required_error: "Select a starting date",
      })
      .refine(
        (date: Date) => isEditing || isTodayOrAfter(date),
        "Start date cannot be in the past",
      )
      .default(startsAtDefaultValue),
    expiresAt: expiresAtDefaultValue
      ? z
          .date()
          .refine(isTodayOrAfter, "Expiration date cannot be in the past")
          .optional()
          .default(expiresAtDefaultValue)
      : z
          .date()
          .refine(isTodayOrAfter, "Expiration date cannot be in the past")
          .optional(),
  });

const StipendFrequencyLabels = {
  [StipendRuleFrequency.OneTime]: "One Time",
  [StipendRuleFrequency.Daily]: "Daily (Only weekdays)",
  [StipendRuleFrequency.Monthly]: "Monthly",
};

type Props = AnimatedProps<{
  onNext: (step: CreateStipendStep) => void;
  onBack: () => void;
  style: CSSProperties;
}> & { isEditing: boolean };

export const DetailsForm = ({ onNext, onBack, style, isEditing }: Props) => {
  const [
    { frequency, isBalanceForwardable, amount, startsAt, expiresAt },
    setCreateOrEditStipendState,
  ] = useRecoilState(createOrEditStipendState);

  const formSchema = getFormSchema(isEditing, startsAt, expiresAt);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      frequency,
      isBalanceForwardable,
      amount,
      startsAt: startsAt || new Date(),
      expiresAt,
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      data.expiresAt &&
      isBefore(endOfDay(data.expiresAt), startOfDay(data.startsAt))
    ) {
      form.setError("expiresAt", {
        type: "manual",
        message: "Expiration date must be after the start date",
      });
      return;
    }
    setCreateOrEditStipendState((prevState) => ({
      ...prevState,
      ...data,
    }));
    onNext(CreateStipendStep.RulePolicy);
  };

  return (
    <animated.div
      style={style}
      className="mt-6 flex w-full flex-grow flex-col pl-6 lg:pl-10"
    >
      <Body size="2xl" weight="medium">
        {isEditing ? "Edit Details" : "Details"}
      </Body>
      <Body size="md" weight="medium" className="mt-3 text-white/70">
        Any details inserted here will automatically appear in the policy we
        generate with you.
      </Body>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-6 flex grow flex-col"
        >
          <div className="flex flex-col gap-10 lg:flex-row">
            <div className="flex-1 space-y-8">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="frequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex justify-between">
                        <span>Frequency</span>
                        <span>Choose one</span>
                      </FormLabel>
                      <FormControl>
                        <div className="flex gap-x-3">
                          {[
                            StipendRuleFrequency.OneTime,
                            StipendRuleFrequency.Daily,
                            StipendRuleFrequency.Monthly,
                          ].map((freq) => (
                            <Button
                              key={`stipend-frequency--frequency-${freq}`}
                              type="button"
                              disabled={isEditing as boolean}
                              className="flex-1"
                              onClick={() => {
                                field.onChange(freq);
                                if (freq === StipendRuleFrequency.OneTime) {
                                  form.setValue("isBalanceForwardable", false);
                                }
                              }}
                              variant={
                                field.value === freq ? "default" : "secondary"
                              }
                            >
                              {StipendFrequencyLabels[freq]}
                            </Button>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isBalanceForwardable"
                  render={({ field }) => {
                    const disabled =
                      form.watch("frequency") ===
                        StipendRuleFrequency.OneTime || isEditing;
                    return (
                      <FormItem>
                        <div className="flex items-center gap-x-3">
                          <FormControl>
                            <Checkbox
                              {...field}
                              value={"on"}
                              checked={field.value}
                              disabled={disabled}
                              onCheckedChange={(value) => {
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormLabel className="flex flex-col gap-y-1">
                            <span
                              className={cn(
                                "text-body-sm font-medium text-white",
                                { "text-white/70": disabled },
                              )}
                            >
                              Stipend Amount Balance Fowardable
                            </span>
                            <span
                              className={cn("text-body-sm text-white/70", {
                                "text-white/50": disabled,
                              })}
                            >
                              Would you like stipend wallet balances to
                              carry-over to the next cycle?
                            </span>
                          </FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              <div className="flex w-full gap-x-4">
                <FormField
                  control={form.control}
                  name="startsAt"
                  render={({ field }) => (
                    <FormItem className="flex-1 shrink-0">
                      <FormLabel className="flex">Starting Date</FormLabel>
                      <FormControl className="flex w-full flex-col">
                        <DatePicker
                          selected={field.value}
                          disabled={isEditing}
                          onSelect={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expiresAt"
                  render={({ field }) => (
                    <FormItem className="flex-1 shrink-0">
                      <FormLabel className="flex justify-between">
                        <span>Expiration Date</span>
                        <span>Optional</span>
                      </FormLabel>
                      <FormControl className="flex w-full flex-col">
                        <DatePicker
                          selected={field.value}
                          onSelect={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem className="w-1/2 pr-2">
                    <FormLabel>Amount (US Dollars)</FormLabel>
                    <FormControl>
                      <CurrencyInput disabled={isEditing} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex-1 space-y-4">
              <Card className="space-y-4 p-6">
                <Body size="md" weight="medium">
                  Frequency
                </Body>
                <div>
                  <Body size="md" weight="medium">
                    One-Time
                  </Body>
                  <Body size="sm" weight="regular" className="text-white/70">
                    Only issued once. The stipend balance will never be
                    re-funded.
                  </Body>
                </div>
                <div>
                  <Body size="md" weight="medium">
                    Daily
                  </Body>
                  <Body size="sm" weight="regular" className="text-white/70">
                    Stipends begin their cycle at 12:00:00 AM EST every week day
                    and end at 11:59:59 PM EST.
                  </Body>
                </div>
                <div>
                  <Body size="md" weight="medium">
                    Monthly
                  </Body>
                  <Body size="sm" weight="regular" className="text-white/70">
                    Stipends begin their cycle on the first of the month at
                    12:00:00 AM EST and end on the last day of the month at
                    11:59:59 PM EST.
                  </Body>
                </div>
              </Card>
              <Card className="space-y-4 p-6">
                <Body size="md" weight="medium">
                  Expiration Date
                </Body>
                <Body size="sm" weight="regular" className="text-white/70">
                  Set an expiration date for a stipend, after which employees
                  will no longer be able to submit claims against it. If no date
                  is provided, the stipend will never expire.{" "}
                </Body>
              </Card>
            </div>
          </div>
          <div className="mt-10 flex flex-grow items-end justify-between">
            <Button type="button" variant="ghost" onClick={onBack}>
              Back
            </Button>
            <Button type="submit" className="w-44">
              Next
            </Button>
          </div>
        </form>
      </Form>
    </animated.div>
  );
};
